import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import { PageContainer, PageHeader } from "../components/PageHeader";
import SignageGroupDialog, {
  type GroupPickerScreen,
} from "../components/SignageGroupDialog";
import SignageGroupDeleteDialog from "../components/SignageGroupDeleteDialog";
import SignageGroupContentDialog from "../components/SignageGroupContentDialog";
import SignageChangeChannelDialog from "../components/SignageChangeChannelDialog";
import { tokens } from "../theme";
import {
  foyerApi,
  type CompanyScreenRecord,
  type ScreenRecord,
  type SignageScopeRef,
} from "../http/foyer";
import type {
  SignageAssignment,
  SignageAssignmentPatch,
  SignageGroupFollowing,
  SignagePlaylist,
  SignageScreenGroup,
} from "@shared/types/signage";

/** What a masked field is called where an operator can see it. */
const FIELD_LABEL: Record<string, string> = {
  weekly: "weekly plan",
  defaultPlaylistId: "default channel",
  override: "takeover",
  soundEnabled: "sound",
  breaksPerHour: "ad breaks",
  breakSeconds: "break length",
  orientation: "orientation",
};

/**
 * "3 of 8 not following", with the reason behind it.
 *
 * ## Why this is a warning and not an error
 *
 * Nothing here is broken. A screen set by hand outranking its group is the
 * layer model working exactly as designed, and an operator who deliberately
 * took one wall over does not want a red row about it. What they cannot see
 * without this is that the group's promise is not being kept everywhere - so
 * the count states the fact and the tooltip names the cause, and neither
 * suggests a fix.
 *
 * ## Why it counts screens but explains per field
 *
 * The count is the thing you scan a table for; the detail is the thing you open
 * when it surprises you. A screen can take the group's channel and ignore its
 * sound, so it appears once in the count and twice in the reasons.
 */
function FollowingNote({
  row,
  screenLabel,
}: {
  row: SignageGroupFollowing | undefined;
  screenLabel: (id: string) => string;
}) {
  // Undefined means "not answered yet" - deliberately silent rather than
  // claiming everything follows.
  if (!row || !row.masked.length) return null;
  const screens = new Set(row.masked.map((m) => m.screenId));

  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <Box sx={{ py: 0.5 }}>
          {row.masked.map((m, i) => (
            <Typography
              key={`${m.screenId}-${m.by}-${i}`}
              sx={{ fontSize: 12.5, lineHeight: 1.6 }}
            >
              <b>{screenLabel(m.screenId)}</b>
              {m.by === "screen"
                ? " was set directly"
                : ` follows “${m.byGroupName ?? "another group"}”`}
              {" — "}
              {m.fields.map((f) => FIELD_LABEL[f] ?? f).join(", ")}
            </Typography>
          ))}
        </Box>
      }
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={0.5}
        sx={{ cursor: "help", color: tokens.warning }}
      >
        <WarningAmberIcon sx={{ fontSize: 15 }} />
        <span style={{ fontSize: 12.5 }}>
          {screens.size} of {row.screens} not following
        </span>
      </Stack>
    </Tooltip>
  );
}

/**
 * Screen groups - a named set of screens, addressed as one thing.
 *
 * ## What the Type column is telling you
 *
 * Which LAYER a group sits on, and therefore who it belongs to. A location sees
 * its own groups, its chain's, and Nenda's - and can only edit its own. That is
 * the same rule as playlists and campaigns, and the column exists because a row
 * you cannot edit is otherwise indistinguishable from one you can until you
 * try.
 *
 * ## Sorting is client-side, deliberately
 *
 * A customer has tens of groups, not thousands. Paging and server-side sort
 * would be two more things to keep in agreement with the layer rules for a list
 * that fits in memory several times over.
 */

type SortKey = "name" | "createdAt" | "updatedAt" | "screens";

const SCOPE_LABEL: Record<string, string> = {
  premise: "Location",
  company: "Company",
  global: "Nenda",
};

/** `2026-05-12` - the column is a date, and a time would only add noise. */
function day(iso: string): string {
  return iso.slice(0, 10);
}

export default function SignageGroupsPage({
  premiseId,
  companyId,
}: {
  /** Exactly one is set: a location, or the whole customer. */
  premiseId?: string;
  companyId?: string;
}) {
  const scope: SignageScopeRef = premiseId
    ? { kind: "premise", premiseId }
    : { kind: "company", companyId: companyId ?? "" };
  const scopeKey = premiseId ?? companyId ?? "";

  const [groups, setGroups] = useState<SignageScreenGroup[]>([]);
  /**
   * Per group, which of its screens are ignoring it.
   *
   * Keyed by group id and allowed to arrive late - the table renders without it
   * and grows the warning when it lands. A masking check that has not answered
   * yet must never look like "nothing is masked", which is why this starts as
   * an empty MAP rather than an empty result per group.
   */
  const [following, setFollowing] = useState<
    Map<string, SignageGroupFollowing>
  >(new Map());
  const [screens, setScreens] = useState<GroupPickerScreen[]>([]);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortKey>("updatedAt");
  const [tick, setTick] = useState(0);

  const [editing, setEditing] = useState<SignageScreenGroup | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState<SignageScreenGroup | null>(null);
  /** The group whose CONTENT is being set, and what it currently says. */
  const [contentFor, setContentFor] = useState<SignageScreenGroup | null>(null);
  const [contentNow, setContentNow] = useState<SignageAssignment | null>(null);
  /** The group being taken over right now, or null. */
  const [takeoverFor, setTakeoverFor] = useState<SignageScreenGroup | null>(
    null
  );
  const [playlists, setPlaylists] = useState<SignagePlaylist[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [menu, setMenu] = useState<{
    el: HTMLElement;
    group: SignageScreenGroup;
  } | null>(null);

  useEffect(() => {
    let alive = true;
    foyerApi
      .listSignageGroupsIn(scope)
      .then((list) => alive && setGroups(list))
      .catch(() => alive && setGroups([]));
    foyerApi
      .listSignageGroupFollowingIn(scope)
      .then(
        (rows) =>
          alive && setFollowing(new Map(rows.map((r) => [r.groupId, r])))
      )
      // Silently: this is an annotation on a table that is already useful, and
      // an error banner over a working page would be the bigger failure.
      .catch(() => alive && setFollowing(new Map()));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.kind, scopeKey, tick]);

  /*
   * The screens the picker can offer, from whichever scope is open.
   *
   * A location lists its own; a customer lists every screen it has, across its
   * locations and any sub-companies. The second is the whole point of a
   * company-layer group - "every reception in the chain" cannot be built from
   * one hotel's list.
   *
   * The location LABEL is only filled in at company scope. Inside one hotel it
   * would be the same word on every row, and the dialog hides its location
   * filter when there is nothing to filter by.
   */
  useEffect(() => {
    let alive = true;
    const load = premiseId
      ? foyerApi.listScreens(premiseId).then((list: ScreenRecord[]) =>
          list.map((s) => ({
            id: s.id,
            label: s.roomId || s.model || s.id,
            code: s.id,
            premiseLabel: "",
          }))
        )
      : foyerApi
          .listCompanyScreens(companyId ?? "")
          .then((list: CompanyScreenRecord[]) =>
            list.map((s) => ({
              id: s.id,
              label: s.roomId || s.model || s.id,
              code: s.id,
              premiseLabel: s.premiseName || s.premiseId,
            }))
          );
    load
      .then((rows) => alive && setScreens(rows))
      .catch(() => alive && setScreens([]));
    return () => {
      alive = false;
    };
  }, [premiseId, companyId, tick]);

  useEffect(() => {
    let alive = true;
    foyerApi
      .listSignagePlaylistsIn(scope)
      .then((list) => alive && setPlaylists(list))
      .catch(() => alive && setPlaylists([]));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.kind, scopeKey, tick]);

  /*
   * Opened per group rather than loaded with the list: most groups are never
   * given content, and one request per row would be a request per row for a
   * page that is mostly read.
   */
  const openContent = async (g: SignageScreenGroup) => {
    setContentFor(g);
    setContentNow(null);
    setError(null);
    try {
      setContentNow(await foyerApi.getSignageGroupAssignmentIn(scope, g.id));
    } catch {
      setError("Could not read what that group plays.");
    }
  };

  const saveContent = async (patch: SignageAssignmentPatch) => {
    if (!contentFor) return;
    setBusy(true);
    setError(null);
    try {
      await foyerApi.putSignageGroupAssignmentIn(scope, contentFor.id, patch);
      setContentFor(null);
      setTick((t) => t + 1);
    } catch {
      setError("Could not save what the group plays.");
    } finally {
      setBusy(false);
    }
  };

  /*
   * Force a channel onto every screen in the group, now.
   *
   * The same override a single screen gets, written one layer up. The resolver
   * already prefers a screen's OWN override to a group's, so a wall somebody
   * took over by hand this morning keeps what they chose - which is the rule
   * everywhere else and would be surprising if it stopped applying here.
   */
  const takeOver = async (playlistId: string, until: string | null) => {
    if (!takeoverFor) return;
    await foyerApi.putSignageGroupAssignmentIn(scope, takeoverFor.id, {
      override: { playlistId, since: new Date().toISOString(), until },
    });
    setTakeoverFor(null);
    setTick((t) => t + 1);
  };

  /**
   * A screen id as somebody would say it out loud.
   *
   * Falls back to the raw id rather than to "Unknown": a screen can be masked
   * while sitting in a location the current scope does not list, and an id you
   * can search for beats a word you cannot.
   */
  const screenLabel = useMemo(() => {
    const byId = new Map(screens.map((s) => [s.id, s.label]));
    return (id: string) => byId.get(id) ?? id;
  }, [screens]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = q
      ? groups.filter((g) => g.name.toLowerCase().includes(q))
      : groups;
    return [...rows].sort((a, b) => {
      switch (sort) {
        case "name":
          return a.name.localeCompare(b.name);
        case "createdAt":
          return b.createdAt.localeCompare(a.createdAt);
        case "screens":
          return b.screenIds.length - a.screenIds.length;
        default:
          return b.updatedAt.localeCompare(a.updatedAt);
      }
    });
  }, [groups, query, sort]);

  const canEdit = (g: SignageScreenGroup) =>
    premiseId ? g.scope === "premise" : g.scope === "company";

  const save = async (input: { name: string; screenIds: string[] }) => {
    setBusy(true);
    setError(null);
    try {
      if (editing) {
        await foyerApi.updateSignageGroupIn(scope, editing.id, input);
      } else {
        await foyerApi.createSignageGroupIn(scope, input);
      }
      setDialogOpen(false);
      setEditing(null);
      setTick((t) => t + 1);
    } catch {
      setError("Could not save the group. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await foyerApi.deleteSignageGroupIn(scope, deleting.id);
      setDeleting(null);
      setTick((t) => t + 1);
    } catch {
      setError("Could not delete that group.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Screen groups"
        subtitle="Name a set of screens so you can address them together."
      />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        gap={1.5}
        sx={{ mb: 2 }}
        alignItems={{ sm: "center" }}
      >
        <TextField
          size="small"
          placeholder="Search screen groups…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          inputProps={{ "aria-label": "Search screen groups" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1, maxWidth: 360 }}
        />
        <Box sx={{ flex: 1 }} />
        <TextField
          select
          size="small"
          value={sort}
          onChange={(e) => setSort(e.target.value as SortKey)}
          inputProps={{ "aria-label": "Sort" }}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="updatedAt">Recently changed</MenuItem>
          <MenuItem value="createdAt">Recently created</MenuItem>
          <MenuItem value="name">Name</MenuItem>
          <MenuItem value="screens">Most screens</MenuItem>
        </TextField>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditing(null);
            setError(null);
            setDialogOpen(true);
          }}
          data-testid="new-group"
        >
          New screen group
        </Button>
      </Stack>

      {error && (
        <Typography sx={{ mb: 2, fontSize: 13, color: tokens.error }}>
          {error}
        </Typography>
      )}

      <Box
        sx={{
          border: `1px solid ${tokens.divider}`,
          borderRadius: "12px",
          overflow: "hidden",
          bgcolor: tokens.surface,
        }}
      >
        {shown.length === 0 ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <Typography sx={{ fontWeight: 600, color: tokens.ink }}>
              {groups.length === 0
                ? "No screen groups yet"
                : "No group matches that search"}
            </Typography>
            {groups.length === 0 && (
              <Typography
                sx={{ fontSize: 13, color: tokens.inkMuted, mt: 0.5 }}
              >
                Group the screens you tend to change together, then address them
                as one.
              </Typography>
            )}
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Changed</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Screens</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((g) => (
                <TableRow key={g.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{g.name}</TableCell>
                  <TableCell sx={{ color: tokens.inkMuted }}>
                    {day(g.createdAt)}
                  </TableCell>
                  <TableCell sx={{ color: tokens.inkMuted }}>
                    {day(g.updatedAt)}
                  </TableCell>
                  <TableCell sx={{ color: tokens.inkMuted }}>
                    {SCOPE_LABEL[g.scope] ?? g.scope}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" gap={0.75}>
                      <TvOutlinedIcon
                        sx={{ fontSize: 18, color: tokens.inkMuted }}
                      />
                      <span>
                        {g.screenIds.length}{" "}
                        {g.screenIds.length === 1 ? "screen" : "screens"}
                      </span>
                    </Stack>
                    <FollowingNote
                      row={following.get(g.id)}
                      screenLabel={screenLabel}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      aria-label={`Actions for ${g.name}`}
                      onClick={(e) =>
                        setMenu({ el: e.currentTarget, group: g })
                      }
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>

      <Typography sx={{ fontSize: 13, color: tokens.inkMuted, mt: 2 }}>
        Showing {shown.length} of {groups.length}{" "}
        {groups.length === 1 ? "screen group" : "screen groups"}
      </Typography>

      <Menu
        open={menu !== null}
        anchorEl={menu?.el}
        onClose={() => setMenu(null)}
      >
        {/*
          A borrowed group offers neither action rather than offering them and
          failing: the server answers 404 for a layer this scope cannot write,
          and a menu that leads to that is a worse explanation than a menu that
          says so by being empty.
        */}
        {menu && canEdit(menu.group)
          ? [
              <MenuItem
                key="takeover"
                onClick={() => {
                  setTakeoverFor(menu.group);
                  setMenu(null);
                }}
              >
                Change channel now
              </MenuItem>,
              <MenuItem
                key="content"
                onClick={() => {
                  void openContent(menu.group);
                  setMenu(null);
                }}
              >
                Set what it plays
              </MenuItem>,
              <MenuItem
                key="edit"
                onClick={() => {
                  setEditing(menu.group);
                  setError(null);
                  setDialogOpen(true);
                  setMenu(null);
                }}
              >
                Edit group
              </MenuItem>,
              <MenuItem
                key="delete"
                sx={{ color: tokens.error }}
                onClick={() => {
                  setDeleting(menu.group);
                  setMenu(null);
                }}
              >
                Delete group
              </MenuItem>,
            ]
          : [
              <MenuItem key="none" disabled>
                {menu ? `Owned by ${SCOPE_LABEL[menu.group.scope]}` : ""}
              </MenuItem>,
            ]}
      </Menu>

      <SignageGroupDialog
        open={dialogOpen}
        editing={editing}
        screens={screens}
        groups={groups}
        saving={busy}
        error={error}
        onClose={() => {
          setDialogOpen(false);
          setEditing(null);
        }}
        onSave={(input) => void save(input)}
      />

      <SignageChangeChannelDialog
        open={takeoverFor !== null}
        targetName={takeoverFor?.name ?? ""}
        targetScreens={takeoverFor?.screenIds.length ?? 0}
        playlists={playlists}
        onClose={() => setTakeoverFor(null)}
        onPlayNow={takeOver}
        onSchedule={() => setTakeoverFor(null)}
      />

      <SignageGroupContentDialog
        group={contentFor}
        current={contentNow}
        playlists={playlists}
        saving={busy}
        error={error}
        onClose={() => setContentFor(null)}
        onSave={(patch) => void saveContent(patch)}
      />

      <SignageGroupDeleteDialog
        group={deleting}
        busy={busy}
        onClose={() => setDeleting(null)}
        onConfirm={() => void remove()}
      />
    </PageContainer>
  );
}
